import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useListChecklistItemsQuery,
  useCreateChecklistItemMutation,
  useUpdateChecklistItemMutation,
} from '../../store/api/localApi';
import { useAuth } from '../../hooks/useAuth';
import { useLocationContext } from '../../hooks/useLocationContext';
import { useToast } from '../../components/Toast';
import { ChecklistItem, ChecklistKind } from '../../types';

const KIND_LABELS: Record<ChecklistKind, string> = {
  [ChecklistKind.OPENING]: 'Opening',
  [ChecklistKind.CLOSING]: 'Closing',
};

const LABEL_MAX_LENGTH = 200;

/**
 * One kind's list (OPENING or CLOSING), plus its "add item" form. `order` is
 * never entered by the admin — a new item is always appended to the end of
 * THIS kind's list (max existing order + 1, or 0 for the first item), per
 * the contract's "assign it in the UI" requirement.
 */
const TemplateSection: React.FC<{
  kind: ChecklistKind;
  locationId: string;
  items: ChecklistItem[];
}> = ({ kind, locationId, items }) => {
  const { showError } = useToast();
  const [createItem, { isLoading: isCreating }] = useCreateChecklistItemMutation();
  const [updateItem] = useUpdateChecklistItemMutation();

  const [newLabel, setNewLabel] = useState('');
  const [newRequired, setNewRequired] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editRequired, setEditRequired] = useState(true);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const sortedItems = useMemo(() => items.slice().sort((a, b) => a.order - b.order), [items]);

  const nextOrder = sortedItems.length > 0 ? sortedItems[sortedItems.length - 1].order + 1 : 0;

  const trimmedNewLabel = newLabel.trim();
  const canAdd =
    trimmedNewLabel.length > 0 && trimmedNewLabel.length <= LABEL_MAX_LENGTH && !isCreating;

  const handleAdd = async () => {
    if (!canAdd) return;
    try {
      await createItem({
        locationId,
        kind,
        label: trimmedNewLabel,
        order: nextOrder,
        isRequired: newRequired,
      }).unwrap();
      setNewLabel('');
      setNewRequired(true);
    } catch (err) {
      showError((err as any)?.data?.error || 'Failed to add the item');
    }
  };

  const startEdit = (item: ChecklistItem) => {
    setEditingId(item.itemId);
    setEditLabel(item.label);
    setEditRequired(item.isRequired);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditLabel('');
  };

  const trimmedEditLabel = editLabel.trim();
  const canSaveEdit = trimmedEditLabel.length > 0 && trimmedEditLabel.length <= LABEL_MAX_LENGTH;

  const handleSaveEdit = async (itemId: string) => {
    if (!canSaveEdit) return;
    try {
      await updateItem({
        itemId,
        label: trimmedEditLabel,
        isRequired: editRequired,
      }).unwrap();
      setEditingId(null);
      setEditLabel('');
    } catch (err) {
      showError((err as any)?.data?.error || 'Failed to save the item');
    }
  };

  const handleRemove = async (itemId: string) => {
    try {
      await updateItem({ itemId, isActive: false }).unwrap();
    } catch (err) {
      showError((err as any)?.data?.error || 'Failed to remove the item');
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <section
      data-testid={`template-section-${kind}`}
      className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
    >
      <h2 className="text-sm font-semibold text-gray-800">{KIND_LABELS[kind]}</h2>

      {sortedItems.length === 0 ? (
        <p data-testid={`template-empty-${kind}`} className="mt-2 text-sm text-gray-500">
          No {KIND_LABELS[kind].toLowerCase()} items set up yet. Add one below — it will appear on
          every staff member's checklist from today onward.
        </p>
      ) : (
        <ul className="mt-2 divide-y divide-gray-100">
          {sortedItems.map((item) => (
            <li key={item.itemId} data-testid={`template-item-row-${item.itemId}`} className="py-2">
              {editingId === item.itemId ? (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    data-testid={`template-edit-label-${item.itemId}`}
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    maxLength={LABEL_MAX_LENGTH}
                    className="flex-1 min-w-[160px] rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <label className="flex items-center gap-1 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      data-testid={`template-edit-required-${item.itemId}`}
                      checked={editRequired}
                      onChange={(e) => setEditRequired(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    Required
                  </label>
                  <button
                    type="button"
                    data-testid={`template-save-${item.itemId}`}
                    onClick={() => handleSaveEdit(item.itemId)}
                    disabled={!canSaveEdit}
                    className="text-xs font-medium text-primary-600 hover:text-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    data-testid={`template-cancel-${item.itemId}`}
                    onClick={cancelEdit}
                    className="text-xs font-medium text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              ) : removingId === item.itemId ? (
                <div className="flex flex-wrap items-center gap-2">
                  <p className="flex-1 min-w-[200px] text-sm text-gray-700">
                    Remove &ldquo;{item.label}&rdquo;? It will stop appearing on future checklists.
                    Past runs that already used it are unaffected and stay exactly as recorded.
                  </p>
                  <button
                    type="button"
                    data-testid={`template-confirm-remove-${item.itemId}`}
                    onClick={() => handleRemove(item.itemId)}
                    className="text-xs font-medium text-red-600 hover:text-red-500"
                  >
                    Remove
                  </button>
                  <button
                    type="button"
                    data-testid={`template-cancel-remove-${item.itemId}`}
                    onClick={() => setRemovingId(null)}
                    className="text-xs font-medium text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <span
                      data-testid={`template-item-label-${item.itemId}`}
                      className="text-sm text-gray-800"
                    >
                      {item.label}
                    </span>
                    {!item.isRequired && (
                      <span className="ml-2 text-xs text-gray-400">Optional</span>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <button
                      type="button"
                      data-testid={`template-edit-${item.itemId}`}
                      onClick={() => startEdit(item)}
                      className="text-xs font-medium text-primary-600 hover:text-primary-500"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      data-testid={`template-remove-${item.itemId}`}
                      onClick={() => setRemovingId(item.itemId)}
                      className="text-xs font-medium text-red-600 hover:text-red-500"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
        <input
          type="text"
          data-testid={`template-new-label-${kind}`}
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder={`Add a new ${KIND_LABELS[kind].toLowerCase()} item…`}
          maxLength={LABEL_MAX_LENGTH}
          className="flex-1 min-w-[200px] rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        <label className="flex items-center gap-1 text-xs text-gray-600">
          <input
            type="checkbox"
            data-testid={`template-new-required-${kind}`}
            checked={newRequired}
            onChange={(e) => setNewRequired(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Required
        </label>
        <button
          type="button"
          data-testid={`template-add-${kind}`}
          onClick={handleAdd}
          disabled={!canAdd}
          className="inline-flex items-center rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isCreating ? 'Adding…' : 'Add item'}
        </button>
      </div>
    </section>
  );
};

/**
 * Admin-only: create/edit/soft-delete the OPENING and CLOSING checklist
 * template items that `DailyChecklistPage` renders for staff. Without this
 * screen a freshly-deployed location has no items and no way to add one —
 * see the daily-checklists UI brief for the incident that made this page
 * necessary.
 *
 * ADMIN-ONLY — same `isAdmin` gate as `ChecklistSignOffQueue`, so a direct
 * URL hit by staff is covered even without the nav link. Both server
 * endpoints are also admin-only, so this is UX, not the enforcement layer.
 */
const ChecklistTemplatePage: React.FC = () => {
  const params = useParams<{ locationId?: string; locId?: string }>();
  const { activeLocationId } = useLocationContext();
  const locationId = params.locationId || params.locId || activeLocationId || undefined;

  const { isAdmin } = useAuth();

  const { data, isLoading, isError, refetch } = useListChecklistItemsQuery(locationId ?? '', {
    skip: !locationId || !isAdmin,
  });

  if (!isAdmin) {
    // Staff must not see this page at all — no template container, no data
    // fetch (the query above is already skipped).
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        You don&apos;t have access to this page.
      </div>
    );
  }

  if (!locationId) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        Invalid URL
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-sm text-slate-500">
        <p className="text-base font-medium text-slate-700">Failed to load checklist items.</p>
        <button
          type="button"
          data-testid="template-retry"
          onClick={() => refetch()}
          className="text-primary-600 hover:text-primary-500 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  const items = data?.items ?? [];
  const openingItems = items.filter((i) => i.kind === ChecklistKind.OPENING);
  const closingItems = items.filter((i) => i.kind === ChecklistKind.CLOSING);

  return (
    // Rendered inside the Checklists tabbed wrapper (admin-only), which supplies
    // the page frame and the "Manage items" tab title, so no outer padding or
    // h1 here — just the descriptive line and the sections.
    <div data-testid="checklist-template-page" className="space-y-6 py-6">
      <p className="text-sm text-gray-600">
        Manage the Opening and Closing items staff answer each day. Removing an item stops it
        appearing on future checklists — it never changes a past run, since every run keeps its own
        snapshot of the items it was answered against.
      </p>

      <TemplateSection kind={ChecklistKind.OPENING} locationId={locationId} items={openingItems} />
      <TemplateSection kind={ChecklistKind.CLOSING} locationId={locationId} items={closingItems} />
    </div>
  );
};

export default ChecklistTemplatePage;
